import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Star, ChevronLeft, MessagesSquare, Send, Bell, BellOff, Trash2, EyeOff, Eye, Pencil, Flag, X, Check } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { ImageUploader, PostImages } from "@/components/ImageUploader";
import { LinkedText } from "@/components/LinkedText";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

export default function ThreadPage() {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [body, setBody] = useState("");
  const [cImages, setCImages] = useState([]);
  const isMember = user?.role === "admin" || user?.subscription_status === "active";
  const isStaff = user?.role === "admin" || user?.role === "hstc_assistant";
  const uid = user?.user_id;
  const [editingThread, setEditingThread] = useState(false);
  const [tEdit, setTEdit] = useState({ title: "", body: "" });
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [cEdit, setCEdit] = useState("");

  const load = useCallback(() => api.get(`/threads/${threadId}`).then(({ data }) => setT(data)).catch(() => navigate("/cases")), [threadId, navigate]);
  useEffect(() => { load(); }, [load]);

  const comment = async () => {
    if (!body.trim()) return;
    try { await api.post(`/threads/${threadId}/comments`, { body, images: cImages }); setBody(""); setCImages([]); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const upvote = async () => { await api.post(`/threads/${threadId}/upvote`); load(); };
  const toggleMute = async () => {
    const next = !t.muted;
    try {
      await api.post(`/threads/${threadId}/mute`, { muted: next });
      setT((prev) => ({ ...prev, muted: next }));
      toast.success(next ? "Muted — no more alerts for this thread" : "Alerts back on for this thread");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteThread = async () => {
    if (!window.confirm("Delete this whole discussion and all its comments? This cannot be undone.")) return;
    try { await api.delete(`/threads/${threadId}`); toast.success("Discussion deleted"); navigate(-1); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteComment = async (id) => {
    if (!window.confirm("Delete this comment permanently?")) return;
    try { await api.delete(`/comments/${id}`); toast.success("Comment deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleHideComment = async (c) => {
    const action = c.hidden ? "unhide" : "hide";
    try {
      await api.post(`/comments/${c.comment_id}/moderate`, { action });
      toast.success(c.hidden ? "Comment restored" : "Comment taken down (hidden from members)");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startEditThread = () => { setTEdit({ title: t.title, body: t.body }); setEditingThread(true); };
  const saveThread = async () => {
    try {
      await api.put(`/threads/${threadId}`, { title: tEdit.title, body: tEdit.body });
      setEditingThread(false); toast.success("Post updated"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startEditComment = (c) => { setEditingCommentId(c.comment_id); setCEdit(c.body); };
  const saveComment = async (id) => {
    try {
      await api.put(`/comments/${id}`, { body: cEdit });
      setEditingCommentId(null); toast.success("Comment updated"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reportThread = async () => {
    const reason = window.prompt("Report this discussion to the moderators. What's wrong? (optional)");
    if (reason === null) return;
    try { await api.post(`/threads/${threadId}/report`, { reason }); toast.success("Reported — thanks. A moderator will review it."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reportComment = async (id) => {
    const reason = window.prompt("Report this comment to the moderators. What's wrong? (optional)");
    if (reason === null) return;
    try { await api.post(`/comments/${id}/report`, { reason }); toast.success("Reported — thanks. A moderator will review it."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!t) return <div className="min-h-screen bg-charcoal"><Navbar /><div className="label-mono animate-pulse py-20 text-center">Loading…</div></div>;

  return (
    <div className="min-h-screen bg-charcoal">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <button onClick={() => navigate(-1)} className="label-mono hover:text-gold transition-colors inline-flex items-center gap-1 mb-6"><ChevronLeft className="w-3 h-3" /> Back</button>

        <div className="card-surface rounded-md p-6">
          <span className="label-mono text-gold">{t.board}</span>
          {editingThread ? (
            <div className="mt-2 space-y-3">
              <Input value={tEdit.title} onChange={(e) => setTEdit((p) => ({ ...p, title: e.target.value }))} className="bg-charcoal border-white/15 text-white font-display text-xl" data-testid="edit-thread-title" />
              <Textarea value={tEdit.body} onChange={(e) => setTEdit((p) => ({ ...p, body: e.target.value }))} rows={5} className="bg-charcoal border-white/15 text-white" data-testid="edit-thread-body" />
              <div className="flex gap-2">
                <Button onClick={saveThread} className="bg-gold text-charcoal hover:bg-gold-hover" data-testid="save-thread-btn"><Check className="w-4 h-4 mr-1" /> Save</Button>
                <Button onClick={() => setEditingThread(false)} variant="outline" className="border-white/15 text-white/80 bg-transparent" data-testid="cancel-thread-btn"><X className="w-4 h-4 mr-1" /> Cancel</Button>
              </div>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl font-bold text-white mt-2">{t.title}</h1>
              <p className="text-white/70 mt-4 leading-relaxed whitespace-pre-line"><LinkedText text={t.body} /></p>
              <PostImages images={t.images} />
            </>
          )}
          <div className="mt-5 flex items-center gap-4 label-mono flex-wrap">
            <Link to={t.author_id ? `/u/${t.author_id}` : "#"} className={`hover:underline ${t.author_role === "admin" ? "text-gold" : ""}`} data-testid="thread-author-link">{t.author_name}{t.author_role === "admin" && " · Detective"}</Link>
            {t.updated_at && <span className="text-white/30">· edited</span>}
            <button onClick={upvote} className="flex items-center gap-1.5 hover:text-gold transition-colors" data-testid="upvote-thread-btn"><Star className="w-4 h-4" /> {t.upvote_count}</button>
            <span className="flex items-center gap-1.5"><MessagesSquare className="w-4 h-4" /> {t.comments.length}</span>
            {isMember && (
              <button onClick={toggleMute} className={`flex items-center gap-1.5 transition-colors ${t.muted ? "text-gold" : "hover:text-gold"}`} data-testid="mute-thread-btn">
                {t.muted ? <><BellOff className="w-4 h-4" /> Muted</> : <><Bell className="w-4 h-4" /> Mute</>}
              </button>
            )}
            <span className="ml-auto flex items-center gap-4">
              {(uid === t.author_id || isStaff) && !editingThread && (
                <button onClick={startEditThread} className="flex items-center gap-1.5 hover:text-gold transition-colors" data-testid="edit-thread-btn"><Pencil className="w-4 h-4" /> Edit</button>
              )}
              {isMember && !isStaff && uid !== t.author_id && (
                <button onClick={reportThread} className="flex items-center gap-1.5 hover:text-gold transition-colors" data-testid="report-thread-btn"><Flag className="w-4 h-4" /> Report</button>
              )}
              {isStaff && (
                <button onClick={deleteThread} className="flex items-center gap-1.5 text-red-400/80 hover:text-red-400 transition-colors" data-testid="delete-thread-btn"><Trash2 className="w-4 h-4" /> Delete</button>
              )}
            </span>
          </div>
        </div>

        <h2 className="label-mono mt-10 mb-4">{t.comments.length} Comments</h2>
        <div className="space-y-3">
          {t.comments.map((c) => (
            <div key={c.comment_id} className={`card-surface rounded-md p-4 ${c.hidden ? "opacity-60 border border-red-400/30" : ""}`} data-testid={`comment-${c.comment_id}`}>
              <div className="flex items-center gap-2 mb-2 label-mono">
                <Link to={c.author_id ? `/u/${c.author_id}` : "#"} className="w-6 h-6 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center text-gold text-xs font-bold">{(c.author_name || "M")[0]}</Link>
                <Link to={c.author_id ? `/u/${c.author_id}` : "#"} className={`hover:underline ${c.author_role === "admin" ? "text-gold" : "text-white/60"}`} data-testid="comment-author-link">{c.author_name}{c.author_role === "admin" && " · Detective"}</Link>
                {c.updated_at && <span className="text-white/25">· edited</span>}
                {c.hidden && <span className="text-[10px] uppercase tracking-wide text-red-400/90 border border-red-400/40 rounded px-1.5 py-0.5">Taken down</span>}
                <span className="ml-auto flex items-center gap-3">
                  {(uid === c.author_id || isStaff) && editingCommentId !== c.comment_id && (
                    <button onClick={() => startEditComment(c)} className="flex items-center gap-1 text-white/40 hover:text-gold transition-colors" data-testid={`edit-comment-${c.comment_id}`}><Pencil className="w-3.5 h-3.5" /> Edit</button>
                  )}
                  {isMember && !isStaff && uid !== c.author_id && (
                    <button onClick={() => reportComment(c.comment_id)} className="flex items-center gap-1 text-white/40 hover:text-gold transition-colors" data-testid={`report-comment-${c.comment_id}`}><Flag className="w-3.5 h-3.5" /> Report</button>
                  )}
                  {isStaff && (
                    <button onClick={() => toggleHideComment(c)} className="flex items-center gap-1 text-white/40 hover:text-gold transition-colors" data-testid={`hide-comment-${c.comment_id}`}>
                      {c.hidden ? <><Eye className="w-3.5 h-3.5" /> Restore</> : <><EyeOff className="w-3.5 h-3.5" /> Take down</>}
                    </button>
                  )}
                  {isStaff && (
                    <button onClick={() => deleteComment(c.comment_id)} className="flex items-center gap-1 text-red-400/70 hover:text-red-400 transition-colors" data-testid={`delete-comment-${c.comment_id}`}><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                  )}
                </span>
              </div>
              {editingCommentId === c.comment_id ? (
                <div className="space-y-2">
                  <Textarea value={cEdit} onChange={(e) => setCEdit(e.target.value)} rows={3} className="bg-charcoal border-white/15 text-white" data-testid={`edit-comment-input-${c.comment_id}`} />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => saveComment(c.comment_id)} className="bg-gold text-charcoal hover:bg-gold-hover" data-testid={`save-comment-${c.comment_id}`}><Check className="w-3.5 h-3.5 mr-1" /> Save</Button>
                    <Button size="sm" onClick={() => setEditingCommentId(null)} variant="outline" className="border-white/15 text-white/80 bg-transparent" data-testid={`cancel-comment-${c.comment_id}`}><X className="w-3.5 h-3.5 mr-1" /> Cancel</Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-white/80 leading-relaxed whitespace-pre-line"><LinkedText text={c.body} /></p>
              )}
              <PostImages images={c.images} />
            </div>
          ))}
          {t.comments.length === 0 && <p className="text-sm text-white/40">Be the first to comment.</p>}
        </div>

        {isMember ? (
          <div className="mt-6 card-surface rounded-md p-4">
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Add an evidence-based comment…" rows={3} className="bg-charcoal border-white/15 text-white mb-3" data-testid="comment-input" />
            <ImageUploader images={cImages} setImages={setCImages} />
            <div className="flex justify-between items-center mt-3">
              <p className="text-[11px] font-mono text-white/40">No accusations, doxxing, or contacting case parties.</p>
              <Button onClick={comment} className="bg-gold text-charcoal hover:bg-gold-hover" data-testid="post-comment-btn"><Send className="w-4 h-4 mr-1" /> Comment</Button>
            </div>
          </div>
        ) : (
          <div className="mt-6 card-surface rounded-md p-6 text-center text-sm text-white/50">Become a member to join the discussion.</div>
        )}
      </div>
    </div>
  );
}
